import React, { useState, useMemo } from 'react';
import {
    Box, Paper, Typography, Grid, TextField, FormControl, InputLabel,
    Select, MenuItem, Chip, Alert, useTheme, alpha, Divider,
    IconButton, Tooltip
} from '@mui/material';
import cronstrue from 'cronstrue';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

// Preset schedules
const presets = [
    { label: 'Every minute', value: '* * * * *' },
    { label: 'Every 5 minutes', value: '*/5 * * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every day at midnight', value: '0 0 * * *' },
    { label: 'Every day at noon', value: '0 12 * * *' },
    { label: 'Every Monday at 9 AM', value: '0 9 * * 1' },
    { label: 'Every weekday at 9 AM', value: '0 9 * * 1-5' },
    { label: 'First day of month', value: '0 0 1 * *' },
    { label: 'Every Sunday at 3 AM', value: '0 3 * * 0' },
    { label: 'Every 15 minutes', value: '*/15 * * * *' },
];

const minuteOptions = [
    { label: 'Every minute (*)', value: '*' },
    { label: 'Every 5 minutes', value: '*/5' },
    { label: 'Every 10 minutes', value: '*/10' },
    { label: 'Every 15 minutes', value: '*/15' },
    { label: 'Every 30 minutes', value: '*/30' },
    ...Array.from({ length: 60 }, (_, i) => ({ label: `At minute ${i}`, value: String(i) }))
];

const hourOptions = [
    { label: 'Every hour (*)', value: '*' },
    { label: 'Every 2 hours', value: '*/2' },
    { label: 'Every 4 hours', value: '*/4' },
    { label: 'Every 6 hours', value: '*/6' },
    { label: 'Every 12 hours', value: '*/12' },
    ...Array.from({ length: 24 }, (_, i) => ({ label: `At ${i}:00`, value: String(i) }))
];

const dayOfMonthOptions = [
    { label: 'Every day (*)', value: '*' },
    ...Array.from({ length: 31 }, (_, i) => ({ label: `Day ${i + 1}`, value: String(i + 1) }))
];

const monthOptions = [
    { label: 'Every month (*)', value: '*' },
    { label: 'January', value: '1' },
    { label: 'February', value: '2' },
    { label: 'March', value: '3' },
    { label: 'April', value: '4' },
    { label: 'May', value: '5' },
    { label: 'June', value: '6' },
    { label: 'July', value: '7' },
    { label: 'August', value: '8' },
    { label: 'September', value: '9' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' },
];

const dayOfWeekOptions = [
    { label: 'Every day (*)', value: '*' },
    { label: 'Weekdays (Mon-Fri)', value: '1-5' },
    { label: 'Weekends (Sat-Sun)', value: '0,6' },
    { label: 'Sunday', value: '0' },
    { label: 'Monday', value: '1' },
    { label: 'Tuesday', value: '2' },
    { label: 'Wednesday', value: '3' },
    { label: 'Thursday', value: '4' },
    { label: 'Friday', value: '5' },
    { label: 'Saturday', value: '6' },
];

// Calculate next N run times
const calculateNextRuns = (cronExp, count = 5) => {
    try {
        const parts = cronExp.split(' ');
        if (parts.length !== 5) return [];

        const [minPart, hrPart, domPart, monPart, dowPart] = parts;
        const runs = [];
        let date = new Date();

        const matchesPart = (value, part, max) => {
            if (part === '*') return true;
            if (part.includes('/')) {
                const interval = parseInt(part.split('/')[1]);
                return value % interval === 0;
            }
            if (part.includes('-')) {
                const [start, end] = part.split('-').map(Number);
                return value >= start && value <= end;
            }
            if (part.includes(',')) {
                return part.split(',').includes(String(value));
            }
            return String(value) === part;
        };

        for (let i = 0; i < 1000 && runs.length < count; i++) {
            date = new Date(date.getTime() + 60000);

            if (
                matchesPart(date.getMinutes(), minPart, 60) &&
                matchesPart(date.getHours(), hrPart, 24) &&
                matchesPart(date.getDate(), domPart, 31) &&
                matchesPart(date.getMonth() + 1, monPart, 12) &&
                matchesPart(date.getDay(), dowPart, 7)
            ) {
                runs.push(new Date(date));
            }
        }

        return runs;
    } catch {
        return [];
    }
};

const CronBuilder = () => {
    const theme = useTheme();
    const [minute, setMinute] = useState('0');
    const [hour, setHour] = useState('*');
    const [dayOfMonth, setDayOfMonth] = useState('*');
    const [month, setMonth] = useState('*');
    const [dayOfWeek, setDayOfWeek] = useState('*');
    const [customCron, setCustomCron] = useState('');
    const [useCustom, setUseCustom] = useState(false);
    const [command, setCommand] = useState('/usr/bin/php /path/to/script.php');
    const [copied, setCopied] = useState(false);
    const [copiedFull, setCopiedFull] = useState(false);

    const cronExpression = useMemo(() => {
        if (useCustom && customCron) return customCron;
        return `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
    }, [minute, hour, dayOfMonth, month, dayOfWeek, customCron, useCustom]);

    const humanReadable = useMemo(() => {
        try {
            return cronstrue.toString(cronExpression, { verbose: true });
        } catch {
            return 'Invalid cron expression';
        }
    }, [cronExpression]);

    const isValid = useMemo(() => {
        try {
            cronstrue.toString(cronExpression);
            return true;
        } catch {
            return false;
        }
    }, [cronExpression]);

    const nextRuns = useMemo(() => calculateNextRuns(cronExpression, 5), [cronExpression]);

    const handlePreset = (preset) => {
        const [min, hr, dom, mon, dow] = preset.split(' ');
        setMinute(min);
        setHour(hr);
        setDayOfMonth(dom);
        setMonth(mon);
        setDayOfWeek(dow);
        setUseCustom(false);
    };

    const handleCopy = (text, setFn) => {
        navigator.clipboard.writeText(text);
        setFn(true);
        setTimeout(() => setFn(false), 2000);
    };

    const fullCommand = `${cronExpression} ${command}`;

    return (
        <>
            <SEO
                title="Cron Expression Builder & Tester | UmbrellaCo"
                description="Visually build, parse, and test cron schedules. Get human-readable descriptions and calculate the next execution times instantly."
                keywords="cron builder, cron tester, crontab generator, cron expression parser, schedule builder"
                url="/dev-tools/cron-builder"
                jsonLd={createSoftwareAppSchema(
                    'Cron Expression Builder',
                    'Build and test cron schedule expressions visually.',
                    'https://umbrellaco.in/dev-tools/cron-builder',
                    ['Cron Job', 'Scheduling', 'Developer Tools']
                )}
            />
            <ToolPageContainer
                title="Cron Expression Builder"
                description="Build, understand, and test cron schedules visually. Easily generate standard crontab formats for your servers and jobs."
                disablePadding
            >
            <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
            <Grid container spacing={3}>
                {/* Left Panel - Builder */}
                <Grid item xs={12} md={7}>
                    {/* Quick Presets */}
                    <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Quick Presets</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {presets.map((preset) => (
                                <Chip
                                    key={preset.value}
                                    label={preset.label}
                                    onClick={() => handlePreset(preset.value)}
                                    variant={cronExpression === preset.value ? 'filled' : 'outlined'}
                                    color={cronExpression === preset.value ? 'primary' : 'default'}
                                    size="small"
                                    sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
                                />
                            ))}
                        </Box>
                    </Paper>

                    {/* Visual Builder */}
                    <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>Visual Builder</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Minute</InputLabel>
                                    <Select value={minute} label="Minute" onChange={(e) => { setMinute(e.target.value); setUseCustom(false); }}>
                                        {minuteOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Hour</InputLabel>
                                    <Select value={hour} label="Hour" onChange={(e) => { setHour(e.target.value); setUseCustom(false); }}>
                                        {hourOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Day of Month</InputLabel>
                                    <Select value={dayOfMonth} label="Day of Month" onChange={(e) => { setDayOfMonth(e.target.value); setUseCustom(false); }}>
                                        {dayOfMonthOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Month</InputLabel>
                                    <Select value={month} label="Month" onChange={(e) => { setMonth(e.target.value); setUseCustom(false); }}>
                                        {monthOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Day of Week</InputLabel>
                                    <Select value={dayOfWeek} label="Day of Week" onChange={(e) => { setDayOfWeek(e.target.value); setUseCustom(false); }}>
                                        {dayOfWeekOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Or Enter Custom Expression</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="* * * * *"
                            value={useCustom ? customCron : cronExpression}
                            onChange={(e) => { setCustomCron(e.target.value); setUseCustom(true); }}
                            sx={{ '& input': { fontFamily: 'monospace' } }}
                        />
                    </Paper>

                    {/* Command Builder */}
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Command to Execute</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={command}
                            onChange={(e) => setCommand(e.target.value)}
                            placeholder="/path/to/script.sh"
                        />

                        <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha(theme.palette.common.black, 0.03), borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">Full Cron Command</Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.5,
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                wordBreak: 'break-all'
                            }}>
                                <code style={{ flex: 1 }}>{fullCommand}</code>
                                <Tooltip title={copiedFull ? 'Copied!' : 'Copy full command'}>
                                    <IconButton size="small" onClick={() => handleCopy(fullCommand, setCopiedFull)}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Panel - Output */}
                <Grid item xs={12} md={5}>
                    {/* Expression Display */}
                    <Paper sx={{
                        p: 2,
                        borderRadius: 2,
                        mb: 2,
                        bgcolor: isValid ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.error.main, 0.05),
                        border: `1px solid ${isValid ? theme.palette.success.main : theme.palette.error.main}`
                    }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Cron Expression</Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '1.25rem',
                            fontWeight: 600
                        }}>
                            {cronExpression}
                            <Tooltip title={copied ? 'Copied!' : 'Copy expression'}>
                                <IconButton size="small" onClick={() => handleCopy(cronExpression, setCopied)} sx={{ ml: 'auto' }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Paper>

                    {/* Human Readable */}
                    <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Human Readable</Typography>
                        <Alert severity={isValid ? 'info' : 'error'} icon={<ScheduleIcon />}>
                            {humanReadable}
                        </Alert>
                    </Paper>

                    {/* Next Runs */}
                    <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlayArrowIcon fontSize="small" /> Next 5 Scheduled Runs
                        </Typography>
                        {nextRuns.length > 0 ? (
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {nextRuns.map((date, idx) => (
                                    <Box component="li" key={idx} sx={{ mb: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                        {date.toLocaleString('en-US', {
                                            weekday: 'short',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Unable to calculate next runs
                            </Typography>
                        )}
                    </Paper>

                    {/* Reference */}
                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.03) }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Format Reference</Typography>
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.6 }}>
                            <code>┌─ minute (0-59)</code><br />
                            <code>│ ┌─ hour (0-23)</code><br />
                            <code>│ │ ┌─ day of month (1-31)</code><br />
                            <code>│ │ │ ┌─ month (1-12)</code><br />
                            <code>│ │ │ │ ┌─ day of week (0-6)</code><br />
                            <code>* * * * *</code>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            </Box>
            </ToolPageContainer>
        </>
    );
};

export default CronBuilder;
